import { Link } from "react-router-dom";
import { APP } from "../constants.js";
import {
  ArrowRightIcon,
  CheckIcon,
  ImageIcon,
  Logo,
  MonitorIcon,
} from "../Components/Icons";
import "./LandingPage.css";
import { useAuth0 } from "@auth0/auth0-react";

const services = [
  {
    name: "HTML5 Display Studio",
    icon: MonitorIcon,
    description:
      "Animated display banner sets across every IAB size — a layout editor with per-banner layers, sequences and motion, then CM360/DV360-ready HTML5 zips, validated before download.",
  },
  {
    name: "Paid Social — GIF",
    icon: ImageIcon,
    description:
      "Square, story, and landscape formats (1080×1080, 1080×1920, 1200×630) built in the very same editor and exported as animated GIFs, ready for Meta and TikTok feeds.",
  },
  {
    name: "Brand folder",
    icon: Logo,
    description:
      "Voice, USPs, banned words, and compliance rules live in one folder and drive every banner — so on-brand and on-policy is the default, not a review step.",
  },
];

const heroPoints = [
  "Deterministic QA — size budgets and policy checked in code",
  "Every export stamped + UTM-tagged for attribution",
  "One editor for display banners and paid-social GIFs",
];

const stats = [
  { value: "11", label: "Ad formats in one editor" },
  { value: "3", label: "Paid-social GIF sizes" },
  { value: "0", label: "External calls in exports" },
];

const worksWith = ["Campaign Manager 360", "Display & Video 360", "Google Ads", "Meta", "TikTok"];

export default function LandingPage() {

    const { loginWithRedirect } = useAuth0();

  return (
    <div className="page">
      {/* Top nav */}
      <header className="site-header">
        <div className="site-header__inner">
          <Link to="/" className="brand">
            <Logo className="brand__logo" />
            <span className="brand__name">{APP.NAME}</span>
          </Link>
          <nav className="site-nav">
            {/* Plain anchor tags, not <Link> — these are same-page section
                jumps. <Link to="#services"> doesn't auto-scroll in React
                Router v6, and resolves relative to whatever route you're on
                (breaking once other routes like /product exist). A plain
                <a href="/#services"> lets the browser handle the scroll
                natively and always returns to the homepage first. */}
            <a href="/#services" className="nav-link">
              Product
            </a>
            <a href="/#pricing" className="nav-link">
              Pricing
            </a>
            <button
              type="button"
              onClick={() => loginWithRedirect()}
              className="btn btn--primary"
            >
              Log in
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div aria-hidden className="hero__grid-bg" />
        <div aria-hidden className="hero__glow" />

        <div className="hero__inner">
          {/* copy */}
          <div>
            <span className="badge">
              <span className="badge__dot" aria-hidden />
              New — HTML5 Display Studio is live
            </span>
            <h1 className="hero__title">
              Every acquisition asset,
              <br />
              <span className="hero__title-accent">stacked</span> to your brand&apos;s spec.
            </h1>
            <p className="hero__subtitle">
              {APP.NAME} turns your brand rules — voice, USPs, banned words, compliance — into
              launch-ready animated display banners and paid-social GIFs. Validated by code, not
              vibes.
            </p>
            <div className="hero__actions">
              <a href="#services" className="btn btn--primary btn--lg">
                See how it works
                <ArrowRightIcon className="icon" />
              </a>
            </div>
            <ul className="hero__checklist">
              {heroPoints.map((point) => (
                <li key={point} className="hero__checklist-item">
                  <CheckIcon className="icon" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* visual — a layered "stack" of assets */}
          <div aria-hidden className="hero__visual">
            {/* back card: brand folder */}
            <div className="stack-card stack-card--folder">
              <div className="folder-header">
                <span className="folder-header__icon">
                  <Logo className="brand__logo" style={{ width: "0.875rem", height: "0.875rem" }} />
                </span>
                Brand folder
              </div>
              <div className="folder-lines">
                <div className="folder-line folder-line--full" />
                <div className="folder-line folder-line--4-5" />
                <div className="folder-line folder-line--2-3" />
              </div>
              <div className="folder-swatches">
                {["#2450ff", "#0b1020", "#e8edff"].map((c) => (
                  <span key={c} className="folder-swatch" style={{ background: c }} />
                ))}
              </div>
            </div>

            {/* mid card: paid-social GIF preview */}
            <div className="stack-card stack-card--gif">
              <div className="gif-card__header">
                <span className="gif-card__size">
                  <ImageIcon style={{ width: "0.875rem", height: "0.875rem" }} /> 1080×1080
                </span>
                <span className="gif-card__badge">GIF</span>
              </div>
              <div className="gif-creative">
                <div className="gif-creative__glow" />
                <div className="gif-creative__eyebrow">New drop</div>
                <div className="gif-creative__headline">
                  Fresh looks,
                  <br />
                  just landed.
                </div>
                <span className="gif-creative__cta">Shop the drop</span>
              </div>
            </div>

            {/* front card: display banner preview (300x250-ish) */}
            <div className="stack-card stack-card--banner">
              <div className="banner-header">
                <span className="banner-header__size">
                  <MonitorIcon style={{ width: "0.875rem", height: "0.875rem" }} /> 300×250
                </span>
                <span className="banner-header__live">
                  <span className="banner-header__live-dot" /> Live preview
                </span>
              </div>
              <div className="banner-creative">
                <div className="banner-creative__glow" />
                <div>
                  <div className="banner-creative__eyebrow">Summer Sale</div>
                  <div className="banner-creative__headline">
                    Up to 40% off,
                    <br />
                    ends Sunday.
                  </div>
                </div>
                <div className="banner-creative__footer">
                  <span className="banner-creative__cta">Shop now</span>
                  <Logo className="banner-creative__logo" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* works-with strip */}
        <div className="works-with">
          <div className="works-with__inner">
            <span className="works-with__label">Built for</span>
            {worksWith.map((name) => (
              <span key={name} className="works-with__item">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="services-section">
        <p className="section-eyebrow">The studio lineup</p>
        <h2 className="section-title">One brand folder. Every acquisition channel.</h2>
        <div className="services-grid">
          {services.map((service) => (
            <article key={service.name} className="service-card">
              <div className="service-card__top">
                <span className="service-icon service-icon--live">
                  <service.icon style={{ width: "1.25rem", height: "1.25rem" }} />
                </span>
                <span className="status-badge status-badge--live">
                  <span className="status-badge__dot" aria-hidden />
                  Live
                </span>
              </div>
              <h3 className="service-card__name">{service.name}</h3>
              <p className="service-card__desc">{service.description}</p>
            </article>
          ))}
        </div>

        {/* stats */}
        <div className="stats-grid">
          {stats.map((s) => (
            <div key={s.label} className="stat-card">
              <div className="stat-card__value">{s.value}</div>
              <div className="stat-card__label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="pricing-section">
        <div className="pricing-section__inner">
          <p className="section-eyebrow">Pricing</p>
          <h2 className="section-title">Simple credits. No seat math.</h2>
          <p className="pricing-lede">
            Final pricing lands with the public launch — early accounts run free.
          </p>
          <div className="pricing-grid">
            <div className="price-card price-card--free">
              <h3 className="price-card__title">Free</h3>
              <p className="price-card__amount">
                $0<span className="price-card__period"> / forever</span>
              </p>
              <ul className="price-features">
                <li className="price-feature">
                  <CheckIcon className="icon" /> 20 credits on signup
                </li>
                <li className="price-feature">
                  <CheckIcon className="icon" /> HTML5 display + paid-social GIF
                </li>
                <li className="price-feature">
                  <CheckIcon className="icon" /> Brand folders with identity capsule
                </li>
              </ul>
              <Link to="/register" className="btn btn--primary price-card__cta">
                Create your workspace
              </Link>
            </div>
            <div className="price-card price-card--pro">
              <h3 className="price-card__title price-card__title--muted">Pro</h3>
              <p className="price-card__amount price-card__amount--muted">TBD</p>
              <p className="price-card__note">
                Monthly credit packs, team seats, and priority generation. Announced at launch.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div aria-hidden className="cta-section__glow" />
        <div className="cta-section__inner">
          <h2 className="cta-section__title">Your next campaign, minus the grind.</h2>
          <p className="cta-section__subtitle">
            Set up a brand folder once. Generate validated, stamped ad assets whenever media is
            ready to spend.
          </p>
          <Link to="/register" className="btn btn--primary btn--lg cta-section__button">
            Get started free
            <ArrowRightIcon className="icon" />
          </Link>
        </div>
      </section>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <span className="footer-brand">
            <Logo className="footer-brand__logo" />
            {APP.NAME}
          </span>
          <span>
            © {new Date().getFullYear()} {APP.SHORT_NAME}. Support: {APP.SUPPORT_EMAIL}
          </span>
        </div>
      </footer>
    </div>
  );
}